import { CSSProperties, FC, memo } from 'react';
import './TypingDotsIndicator.scss';

interface TypingDotsIndicatorProps {
  isActive?: boolean;
  size?: number;
  color?: string;
  style?: CSSProperties;
  /** Time each dot spends lit (its brighten + dim), in ms. */
  duration?: number;
  className?: string;
  'aria-label'?: string;
}

const DOT_COUNT = 3;

/**
 * Three dots that light one at a time, left to right.
 * Mirrors the React Native TypingDotsIndicator: each dot owns one slot of
 * the cycle (brighten, then dim) and stays at rest opacity the rest of the
 * time, so the pulses never overlap.
 */
const TypingDotsIndicator: FC<TypingDotsIndicatorProps> = memo(
  ({
    isActive = true,
    size = 7,
    color = 'var(--color-primary)',
    style,
    duration = 260,
    className,
    'aria-label': ariaLabel,
  }) => {
    const cycleMs = duration * DOT_COUNT;

    return (
      <div
        className={className ? `typing-dots-indicator ${className}` : 'typing-dots-indicator'}
        style={style}
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
      >
        {Array.from({ length: DOT_COUNT }, (_, index) => (
          <span
            key={index}
            className={
              isActive
                ? 'typing-dots-indicator__dot typing-dots-indicator__dot--active'
                : 'typing-dots-indicator__dot'
            }
            style={{
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: color,
              animationDuration: isActive ? `${cycleMs}ms` : undefined,
              animationDelay: isActive ? `${index * duration}ms` : undefined,
            }}
          />
        ))}
      </div>
    );
  }
);

TypingDotsIndicator.displayName = 'TypingDotsIndicator';

export default TypingDotsIndicator;
